import {
  CustomFieldEntityEnum,
  IBillGroupResp,
  ICustomFieldRecordSchema,
  IInvoiceRespSchema,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { useMemo } from 'react';
import { useGetById } from '../../../api/queryUtils';
import {
  DynamicLabelValueBanner,
  LabelValueLinkItem,
} from '../../../components/DynamicLabelValueBanner/DynamicLabelValueBanner';
import { MCard, MFlex, MSkeleton } from '../../../components/Monetize';
import {
  getBillGroupPageRoute,
  getInvoiceDetailRoute,
} from '../../../constants/routes';
import { useCustomFields } from '../../../hooks/useCustomFields';
import { colors } from '../../../styles/theme';
import { handleCustomFieldToDisplayBannerFormat } from '../../../utils/customFields';

interface CreditAdditionalFieldsBannerProps {
  invoice?: Maybe<Pick<IInvoiceRespSchema, 'invoiceNumber' | 'id'>>;
  billGroupId?: string;
  isLoading: boolean;
  customFields: ICustomFieldRecordSchema;
  reason: string;
  isEditable?: boolean;
  additionalValues?: LabelValueLinkItem[];
  creditType: CustomFieldEntityEnum;
  onOpen: () => void;
  voidReason: string;
}

/**
 * This banner works for Credits and Credit Notes
 */
export const CreditAdditionalFieldsBanner = ({
  invoice,
  billGroupId,
  customFields,
  isLoading,
  reason,
  isEditable = true,
  additionalValues,
  voidReason = '',
  creditType,
  onOpen,
}: CreditAdditionalFieldsBannerProps) => {
  const { data: billGroup } = useGetById<IBillGroupResp>(
    'billGroups',
    billGroupId!,
    {
      enabled: !!billGroupId,
    },
  );

  const { customFieldList, isLoading: isCustomFieldLoading } =
    useCustomFields(creditType);

  const items = useMemo(() => {
    const values = [...(additionalValues ?? [])];

    if (invoice) {
      values.push({
        label: 'Created From Invoice',
        value: invoice.invoiceNumber ?? invoice.id,
        link: getInvoiceDetailRoute(invoice.id),
      });
    }

    if (billGroup) {
      values.push({
        label: 'Bill Group',
        value: billGroup.name,
        link: getBillGroupPageRoute(billGroup.id),
      });
    }

    if (reason) {
      values.push({
        label: 'Credit Reason',
        value: reason,
      });
    }

    if (voidReason) {
      values.push({
        label: 'Void Reason',
        value: voidReason,
      });
    }

    return [
      ...values,
      ...handleCustomFieldToDisplayBannerFormat(customFields, customFieldList),
    ];
  }, [
    additionalValues,
    invoice,
    billGroup,
    customFieldList,
    customFields,
    reason,
    voidReason,
  ]);

  if (isCustomFieldLoading || isLoading) {
    return (
      <MCard
        width={'100%'}
        p={6}
        borderRadius="lg"
        borderColor={colors.tGray.lightPurple}
      >
        <MSkeleton height={4} width="140px" mb={4} />
        <MFlex gap={8}>
          <MSkeleton height={5} width="120px" />
          <MSkeleton height={5} width="120px" />
          <MSkeleton height={5} width="120px" />
        </MFlex>
      </MCard>
    );
  }

  return (
    <DynamicLabelValueBanner
      title="Additional Fields"
      items={items}
      isEditable={isEditable}
      onEditClick={onOpen}
    />
  );
};
